import LegalDocument, { LegalSection } from '@/components/legal-document';
import { formatMoney, formatPrice } from '@/lib/format';
import { privacy_path } from '@/rails/routes';
import type { Plan } from '@/types/platform';
import { Link } from '@inertiajs/react';

const CONTACT_EMAIL = 'lance.foley@caprocklabs.com';
const TEXT_LINK =
  'underline decoration-accent underline-offset-4 hover:decoration-foreground rounded-sm focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-ring';

interface Props {
  updatedOn: string;
  plans: Plan[];
}

function priceLine(plan: Plan): string {
  if (!plan.paid) return 'Free';

  return `${formatPrice(plan.monthlyPriceCents)} a month, or ${formatPrice(plan.yearlyPriceCents)} a year`;
}

function overageLine(plan: Plan): string | null {
  if (plan.storageOverageCentsPerGb === null || plan.emailOverageCentsPerThousand === null) {
    return null;
  }

  return `${plan.name} keeps running past its limits. Extra use costs ${formatMoney(plan.storageOverageCentsPerGb)} for each extra GB of attachments and ${formatMoney(plan.emailOverageCentsPerThousand)} for each extra 1,000 emails.`;
}

const Show = ({ updatedOn, plans }: Props) => {
  const overages = plans.map(overageLine).filter((line): line is string => line !== null);

  return (
    <LegalDocument
      eyebrow="Terms"
      title="Terms of service"
      updatedOn={updatedOn}
      summary={
        <p>
          You own your apps and their data. You pay for your plan through Stripe, and you can cancel
          at any time. When your app takes a payment, the money goes to your own Stripe or Square
          account, and CAP Rock Labs takes no part of it.
        </p>
      }
    >
      <LegalSection number={1} title="This agreement">
        <p>
          These terms are an agreement between you and CAP Rock Labs, of Litchfield, Minnesota. When
          you create an account or use the service, you agree to them. When you use the service for
          a company, you confirm that you can agree for that company.
        </p>
        <p>
          The{' '}
          <Link href={privacy_path()} className={TEXT_LINK}>
            privacy policy
          </Link>{' '}
          says what data we collect and who receives it.
        </p>
      </LegalSection>

      <LegalSection number={2} title="Your account">
        <ul>
          <li>Give true details when you create the account.</li>
          <li>Keep your password secret. You are responsible for what happens in your account.</li>
          <li>Tell us at once if someone uses your account without permission.</li>
        </ul>
      </LegalSection>

      <LegalSection number={3} title="Plans and billing">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[24rem] text-left text-sm">
            <thead>
              <tr className="border-b">
                <th className="py-2 pr-4 font-medium">Plan</th>
                <th className="py-2 pr-4 font-medium">Price</th>
                <th className="py-2 font-medium">Apps</th>
              </tr>
            </thead>
            <tbody>
              {plans.map((plan) => (
                <tr key={plan.key} className="border-b">
                  <td className="py-2 pr-4 font-medium">{plan.name}</td>
                  <td className="text-muted-foreground py-2 pr-4">{priceLine(plan)}</td>
                  <td className="text-muted-foreground py-2 tabular-nums">{plan.appLimit}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <ul>
          <li>Stripe bills a paid plan before each month or year. It renews until you cancel.</li>
          <li>
            When you move to a higher plan, Stripe charges the difference at once. When the charge
            fails, the plan does not change.
          </li>
          <li>
            You can cancel on the Plan and usage page. A paid plan continues to the end of the
            period that you paid for. Then the account moves to the free plan.
          </li>
          <li>We do not refund part of a period.</li>
          {overages.map((line) => (
            <li key={line}>{line}</li>
          ))}
          <li>When a price changes, we tell account owners by email 30 days before.</li>
          <li>Prices do not include tax. You pay any tax that applies.</li>
        </ul>
      </LegalSection>

      <LegalSection number={4} title="Payments in your apps">
        <ul>
          <li>
            When your app takes a payment, you are the seller. The payment goes to your own Stripe
            or Square account.
          </li>
          <li>
            The terms of Stripe or Square apply to those payments. CAP Rock Labs takes no fee and is
            not a party to your sales.
          </li>
          <li>You handle refunds, disputes, chargebacks, and tax on your sales.</li>
        </ul>
      </LegalSection>

      <LegalSection number={5} title="Connected accounts">
        <p>
          When you connect Stripe, Square, or QuickBooks, you let CAP Rock Labs act on that account
          only for the actions that you start: make a pay link when you send an invoice, and send an
          invoice to QuickBooks when you click Send to QuickBooks. You can disconnect at any time on
          the Connections page.
        </p>
      </LegalSection>

      <LegalSection number={6} title="Your content">
        <ul>
          <li>You own the data in your apps.</li>
          <li>
            You let CAP Rock Labs store, copy, and process that data only to run the service for
            you.
          </li>
          <li>
            You are responsible for your right to collect your customers&apos; data, and for telling
            them how you use it.
          </li>
        </ul>
      </LegalSection>

      <LegalSection number={7} title="AI drafts">
        <p>
          The draft writer suggests lists and fields. A draft changes nothing until a person accepts
          it. Check each draft before you accept it.
        </p>
      </LegalSection>

      <LegalSection number={8} title="Use that is not permitted">
        <ul>
          <li>Use the service for something that is against the law.</li>
          <li>Send spam, or send email to people who did not agree to receive it.</li>
          <li>Upload malware, or try to get into another account or app.</li>
          <li>Try to stop the service, or test its security without our written permission.</li>
        </ul>
      </LegalSection>

      <LegalSection number={9} title="Client work">
        <p>
          When CAP Rock Labs builds software for you as a client, a separate written agreement can
          apply. Where the two do not agree, the written agreement applies.
        </p>
      </LegalSection>

      <LegalSection number={10} title="Changes to the service">
        <p>
          We work to keep the service available, but we do not promise that it never stops. We can
          change or remove a feature. For a large change, we tell account owners first.
        </p>
      </LegalSection>

      <LegalSection number={11} title="Ending the agreement">
        <ul>
          <li>You can cancel and close your account at any time.</li>
          <li>
            We can stop an account that breaks these terms, does not pay, or puts other people at
            risk.
          </li>
          <li>Before you close an account, email us for a copy of your data.</li>
        </ul>
      </LegalSection>

      <LegalSection number={12} title="No warranty">
        <p>
          The service is provided as it is. To the extent the law permits, CAP Rock Labs gives no
          warranty that it fits a particular purpose.
        </p>
      </LegalSection>

      <LegalSection number={13} title="Limit of liability">
        <p>
          To the extent the law permits, CAP Rock Labs is not liable for indirect or consequential
          loss, such as lost profit. The total liability of CAP Rock Labs is limited to the fees
          that you paid in the 12 months before the claim.
        </p>
      </LegalSection>

      <LegalSection number={14} title="Law">
        <p>The law of Minnesota applies. Courts in Minnesota hear any dispute.</p>
      </LegalSection>

      <LegalSection number={15} title="Changes to these terms">
        <p>
          When these terms change, we update this page and the date at the top. For a large change,
          we email account owners. For questions, email{' '}
          <a href={`mailto:${CONTACT_EMAIL}`} className={TEXT_LINK}>
            {CONTACT_EMAIL}
          </a>
          .
        </p>
      </LegalSection>
    </LegalDocument>
  );
};

export default Show;
